import { FC, useMemo, useState } from 'react';
import {
    Button,
    ButtonSizes,
    ButtonStyles,
    Checkbox,
    Dropdown,
    Icons,
    Label,
    showAlert,
    SwitcherTab,
    TextInput
} from '@silverfort/sf-components';
import ReportBase from './ReportBase';
import { CustomControl, CyberInsuranceMetadata, ReportType } from '../types';
import { DEFAULT_CYBER_INSURANCE_METADATA } from '../consts';
import { useReportsDrawer } from '../ReportsDrawerContext';

const EMPTY_CUSTOM_CONTROL: CustomControl = { name: '', policies: [], selectedPolicies: [] };

const CyberInsurance: FC = () => {
    const { rulesOptions, generateReport, downloadReport } = useReportsDrawer();
    const [reportMetadata, setReportMetadata] = useState<CyberInsuranceMetadata>(DEFAULT_CYBER_INSURANCE_METADATA);
    const [isLoading, setIsLoading] = useState(false);
    const [downloadableFileData, setDownloadableFileData] = useState<{fileName: string, date: string} | null>(null);

    const updateReportMetadata = <T extends keyof CyberInsuranceMetadata>(
        key: T,
        value: CyberInsuranceMetadata[T] | null
    ) => {
        setReportMetadata((prevState) => ({ ...prevState, [key]: value }));
    };

    const updatePolicyData = (key: keyof CyberInsuranceMetadata, value: string[]) => {
        const selectedPolicies = (value as string[]) ?? [];
        const selected = selectedPolicies.length > 0;
        const policies = selectedPolicies.flatMap((item) => item.split(','));

        updateReportMetadata(key, { selected, selectedPolicies, policies });
    };

    const onGenerateReport = async () => {
        setIsLoading(true);
        const generatedReport = await generateReport({ type: ReportType.CyberInsurance, metadata: reportMetadata });
        if (generatedReport && generatedReport.fileGenerated) {
            setDownloadableFileData(generatedReport);
        } else {
            setDownloadableFileData(null);
            showAlert('Error generating Cyber Insurance Report', 'error');
        }
        setIsLoading(false);
    };

    const onDownloadReport = () => {
        if (downloadableFileData) {
            setIsLoading(true);
            const isDownloaded = downloadReport(downloadableFileData);
            if (isDownloaded) {
                setDownloadableFileData(null);
                showAlert('Cyber Insurance Report pdf downloaded successfully', 'success');
            } else {
                showAlert('Error downloading Cyber Insurance Report', 'error');
            }
            setIsLoading(false);
        }
    };

    const isValid = useMemo(() => {
        if (reportMetadata.emailPolicies?.selected && !reportMetadata.emailPolicies?.policies.length) {
            return false;
        }
        if (reportMetadata.vpnPolicies?.selected && !reportMetadata.vpnPolicies?.policies.length) {
            return false;
        }
        for (const control of reportMetadata.customControls) {
            if (!control.name || !control.policies.length) {
                return false;
            }
        }
        return true;
    }, [reportMetadata]);

    const footerContent = useMemo(
        () =>
            isLoading ? (
                <Button isLoading iconLeft={Icons.Loader} buttonStyle={ButtonStyles.Primary} />
            ) : downloadableFileData ? (
                <Button text="Download" onClick={onDownloadReport} buttonStyle={ButtonStyles.Primary} />
            ) : (
                <Button
                    text="Generate report"
                    buttonStyle={ButtonStyles.Primary}
                    disabled={!isValid}
                    onClick={onGenerateReport}
                />
            ),
        [isLoading, isValid, downloadableFileData]
    );

    return (
        <ReportBase
            headerTitle="Generate a cyber insurance report"
            headerDescription="Generate and download a cyber insurance report (pdf)"
            footerContent={footerContent}>
            <div className="flex flex-col gap-2">
                <Label text="Security controls" />
                <div className="flex flex-col p-4 gap-4 border border-sf-gray-100 rounded-lg">
                    <div className="flex flex-col gap-2">
                        <Checkbox
                            label="Email service policies"
                            checked={reportMetadata.emailPolicies.selected}
                            onChange={() =>
                                updateReportMetadata('emailPolicies', {
                                    ...reportMetadata.emailPolicies,
                                    selected: !reportMetadata.emailPolicies.selected
                                })
                            }
                        />
                        <Dropdown
                            variant="trigger"
                            items={rulesOptions}
                            selectedItems={reportMetadata.emailPolicies.selectedPolicies}
                            onChange={(value) => updatePolicyData('emailPolicies', value as string[])}
                            multiple
                            className="w-full"
                            fullMenuWidth
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Checkbox
                            label="VPN service policies"
                            checked={reportMetadata.vpnPolicies.selected}
                            onChange={() =>
                                updateReportMetadata('vpnPolicies', {
                                    ...reportMetadata.vpnPolicies,
                                    selected: !reportMetadata.vpnPolicies.selected
                                })
                            }
                        />
                        <Dropdown
                            variant="trigger"
                            items={rulesOptions}
                            selectedItems={reportMetadata.vpnPolicies.selectedPolicies}
                            onChange={(value) => updatePolicyData('vpnPolicies', value as string[])}
                            multiple
                            className="w-full"
                            fullMenuWidth
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Checkbox
                            label="NHI with passwords older than"
                            checked={reportMetadata.oldPassword.selected}
                            onChange={() =>
                                updateReportMetadata('oldPassword', {
                                    ...reportMetadata.oldPassword,
                                    selected: !reportMetadata.oldPassword.selected
                                })
                            }
                        />
                        <SwitcherTab
                            items={[
                                { text: '3 mo', value: 3 },
                                { text: '6 mo', value: 6 },
                                { text: '1 yr', value: 12 }
                            ]}
                            onSelect={(value) => updateReportMetadata('oldPassword', { value, selected: true })}
                            selectedItem={reportMetadata.oldPassword.value}
                            tabClassName="w-full"
                        />
                    </div>
                </div>
            </div>
            <div className="flex flex-col">
                <Label text="Custom controls" className="mb-2" />
                {reportMetadata.customControls.map((control, index) => (
                    <CustomControlField
                        key={index}
                        name={control.name}
                        setName={(value) => {
                            const newControls: CustomControl[] = [...reportMetadata.customControls];
                            newControls[index].name = value;
                            updateReportMetadata('customControls', newControls);
                        }}
                        policies={control.selectedPolicies}
                        options={rulesOptions}
                        setPolicies={(value) => {
                            const newControls: CustomControl[] = [...reportMetadata.customControls];
                            newControls[index].selectedPolicies = value;
                            newControls[index].policies = value.flatMap((item) => item.split(','));
                            updateReportMetadata('customControls', newControls);
                        }}
                        onDelete={() =>
                            updateReportMetadata(
                                'customControls',
                                reportMetadata.customControls.filter((_, i) => i !== index)
                            )
                        }
                    />
                ))}
                <Button
                    text="Add"
                    iconLeft={Icons.Add}
                    buttonStyle={ButtonStyles.Primary}
                    buttonSize={ButtonSizes.Small}
                    onClick={() =>
                        updateReportMetadata('customControls', [
                            ...reportMetadata.customControls,
                            { ...EMPTY_CUSTOM_CONTROL }
                        ])
                    }
                />
            </div>
            <div className="flex flex-col gap-2">
                <Label text="Logo" />
                <Checkbox
                    label="Use company logo from settings"
                    checked={reportMetadata.useCustomerIcon}
                    onChange={() => updateReportMetadata('useCustomerIcon', !reportMetadata.useCustomerIcon)}
                />
            </div>
        </ReportBase>
    );
};

interface CustomControlFieldProps {
    name: string;
    policies: string[];
    setName: (value: string) => void;
    setPolicies: (value: string[]) => void;
    onDelete: () => void;
    options: { value: string; title: string }[];
}

const CustomControlField: FC<CustomControlFieldProps> = ({
    name,
    policies,
    setName,
    setPolicies,
    onDelete,
    options
}) => {
    return (
        <div className="flex flex-col p-4 gap-4 border border-sf-gray-100 rounded-lg mb-2">
            <div className="flex flex-col gap-2">
                <TextInput onChange={setName} className="w-full" value={name} />
                <Dropdown
                    variant="trigger"
                    items={options}
                    selectedItems={policies}
                    onChange={(value) => setPolicies((value as string[]) ?? [])}
                    multiple
                    size="large"
                    className="w-full"
                    fullMenuWidth
                />
                <Button
                    text="Delete"
                    iconRight={Icons.Delete}
                    buttonStyle={ButtonStyles.Tertiary}
                    buttonSize={ButtonSizes.Small}
                    className="self-end"
                    onClick={onDelete}
                />
            </div>
        </div>
    );
};

export default CyberInsurance;
